
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TagListProps {
  tags: string[] | {
    name?: string;
    sys?: {
      id?: string;
    };
  }[] | any[];
  variant?: 'default' | 'sidebar';
  onTagClick?: (tag: string) => void;
}

const TagList: React.FC<TagListProps> = ({
  tags,
  variant = 'default',
  onTagClick
}) => {
  const navigate = useNavigate();

  // Convert tags to a consistent format (array of strings)
  const normalizedTags: string[] = React.useMemo(() => {
    if (!tags || !Array.isArray(tags)) {
      return [];
    }
    return tags.map(tag => {
      // Handle different tag formats
      if (typeof tag === 'string') {
        return tag;
      } else if (tag && typeof tag === 'object') {
        // Handle Contentful tag format: { name: string, ... }
        if ('name' in tag && typeof tag.name === 'string') {
          return tag.name;
        }
        // Try to get sys.id for Contentful metadata tags
        if ('sys' in tag && tag.sys && typeof tag.sys === 'object' && 'id' in tag.sys && typeof tag.sys.id === 'string') {
          return tag.sys.id;
        }
      }
      return '';
    }).filter(Boolean); // Remove empty tags
  }, [tags]);

  // Helper function to convert string to sentence case
  const toSentenceCase = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleTagClick = (tag: string) => {
    if (onTagClick) {
      onTagClick(tag);
    } else {
      // Default behavior: navigate to search page with tag as query parameter
      navigate(`/search?tag=${encodeURIComponent(tag)}`);
    }
  };

  if (normalizedTags.length === 0) {
    console.log('TagList - No tags to display:', tags);
    return null; // Don't render anything if there are no valid tags
  }

  if (variant === 'sidebar') {
    return (
      <div className="flex flex-wrap gap-2">
        {normalizedTags.map(tag => (
          <Button
            key={tag}
            variant="outline"
            size="sm"
            onClick={() => handleTagClick(tag)}
            className={cn(
              "whitespace-nowrap rounded-full border hover:bg-gray-200 transition-colors text-xs",
              "bg-transparent hover:text-black cursor-pointer font-normal"
            )}
          >
            {toSentenceCase(tag)}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-8 mb-8">
      <div className="flex flex-wrap gap-2">
        {normalizedTags.map(tag => (
          <Button
            key={tag}
            variant="outline"
            size="sm"
            onClick={() => handleTagClick(tag)}
            className={cn(
              "whitespace-nowrap rounded-full border hover:bg-gray-200 transition-colors text-xs",
              "bg-transparent hover:text-black cursor-pointer font-normal"
            )}
          >
            {toSentenceCase(tag)}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default TagList;
